import React from 'react';

// ── BUTTON ──────────────────────────────────────────────
export function Btn({ children, variant='primary', size='md', loading, onClick, type='button', className='' }) {
  const base = 'inline-flex items-center gap-2 font-semibold rounded-lg transition-all duration-150 disabled:opacity-50 cursor-pointer';
  const sizes = { sm:'px-3 py-1.5 text-xs', md:'px-4 py-2 text-sm', lg:'px-6 py-2.5 text-base' };
  const variants = {
    primary:  'bg-blue-600 text-white hover:bg-blue-700 shadow-sm',
    success:  'bg-emerald-600 text-white hover:bg-emerald-700',
    danger:   'bg-red-600 text-white hover:bg-red-700',
    warn:     'bg-amber-500 text-white hover:bg-amber-600',
    outline:  'border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 bg-white',
    ghost:    'bg-slate-100 text-slate-600 hover:bg-slate-200',
  };
  return (
    <button type={type} onClick={onClick} disabled={loading}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}>
      {loading && <span className="animate-spin">⟳</span>}
      {children}
    </button>
  );
}

// ── BADGE ───────────────────────────────────────────────
const badgeColors = {
  blue:'bg-blue-100 text-blue-700', green:'bg-emerald-100 text-emerald-700',
  red:'bg-red-100 text-red-700', yellow:'bg-amber-100 text-amber-700',
  purple:'bg-violet-100 text-violet-700', gray:'bg-slate-100 text-slate-600',
  orange:'bg-orange-100 text-orange-700',
};
export function Badge({ children, color='gray' }) {
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${badgeColors[color]}`}>{children}</span>;
}

// ── STATUS BADGE map ────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    draft:['gray','Draft'], confirmed:['blue','Confirmed'], po_raised:['purple','PO Raised'],
    shipped:['blue','Shipped'], invoiced:['green','Invoiced'], closed:['green','Closed'],
    cancelled:['red','Cancelled'], sent:['blue','Sent'], grn_done:['green','GRN Done'],
    billed:['green','Billed'], pending:['yellow','Pending'], received:['green','Received'],
    unpaid:['yellow','Unpaid'], paid:['green','Paid'], partial:['orange','Partial'],
    booked:['blue','Booked'], in_transit:['blue','In Transit'], delivered:['green','Delivered'],
    leo:['green','LEO'], active:['green','Active'], expired:['red','Expired'],
    filed:['blue','Filed'], accepted:['green','Accepted'],
  };
  const [color, label] = map[status] || ['gray', status];
  return <Badge color={color}>{label}</Badge>;
}

// ── CARD ────────────────────────────────────────────────
export function Card({ children, className='' }) {
  return <div className={`bg-white border border-slate-200 rounded-xl shadow-sm ${className}`}>{children}</div>;
}
export function CardHeader({ title, actions, subtitle }) {
  return (
    <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
      <div className="flex-1">
        <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

// ── TABLE ───────────────────────────────────────────────
export function Table({ headers, children, empty='No records found.' }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {headers.map((h,i) => (
              <th key={i} className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-400 whitespace-nowrap ${h.right ? 'text-right' : ''}`}>
                {typeof h === 'string' ? h : h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {React.Children.count(children) === 0
            ? <tr><td colSpan={headers.length} className="text-center py-10 text-slate-400 text-sm">{empty}</td></tr>
            : children}
        </tbody>
      </table>
    </div>
  );
}
export function Tr({ children, onClick }) {
  return <tr onClick={onClick} className={`border-b border-slate-100 hover:bg-blue-50/40 transition-colors ${onClick ? 'cursor-pointer' : ''}`}>{children}</tr>;
}
export function Td({ children, right, mono }) {
  return <td className={`px-4 py-3 text-slate-600 ${right ? 'text-right' : ''} ${mono ? 'font-mono text-xs' : ''}`}>{children}</td>;
}

// ── FORM ────────────────────────────────────────────────
export function FormGroup({ label, required, error, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>}
      {children}
      {hint && <span className="text-xs text-slate-400">{hint}</span>}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}
const inputBase = 'w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition placeholder-slate-400';
export function Input({ ...p }) { return <input className={inputBase} {...p} />; }
export function Select({ children, ...p }) { return <select className={inputBase} {...p}>{children}</select>; }
export function Textarea({ ...p }) { return <textarea className={`${inputBase} resize-y min-h-[70px]`} {...p} />; }

// ── MODAL ───────────────────────────────────────────────
export function Modal({ open, onClose, title, children, footer, size='md' }) {
  if (!open) return null;
  const widths = { sm:'max-w-sm', md:'max-w-lg', lg:'max-w-2xl', xl:'max-w-4xl', full:'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${widths[size]} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <h2 className="font-bold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
        </div>
        <div className="overflow-y-auto px-6 py-5 flex-1">{children}</div>
        {footer && <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
}

// ── STAT CARD ───────────────────────────────────────────
export function StatCard({ icon, label, value, sub, color='blue' }) {
  const colors = { blue:'border-t-blue-500', green:'border-t-emerald-500', amber:'border-t-amber-500', purple:'border-t-violet-500', red:'border-t-red-500' };
  return (
    <Card className={`border-t-4 ${colors[color]} p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{label}</p>
          <p className="text-2xl font-black text-slate-800 tracking-tight">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <span className="text-2xl opacity-20">{icon}</span>
      </div>
    </Card>
  );
}

// ── ALERT ───────────────────────────────────────────────
export function Alert({ children, type='info' }) {
  const s = { info:'bg-blue-50 border-blue-200 text-blue-800', success:'bg-emerald-50 border-emerald-200 text-emerald-800', warn:'bg-amber-50 border-amber-200 text-amber-800', error:'bg-red-50 border-red-200 text-red-800' };
  return <div className={`flex gap-2 items-start p-3 rounded-lg border text-sm ${s[type]} mb-3`}>{children}</div>;
}

// ── SPINNER ─────────────────────────────────────────────
export function Spinner() {
  return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
}

// ── SECTION DIVIDER ─────────────────────────────────────
export function SectionDivider({ title }) {
  return (
    <div className="flex items-center gap-3 my-4">
      <span className="text-xs font-bold uppercase tracking-widest text-blue-600 whitespace-nowrap">{title}</span>
      <div className="flex-1 h-px bg-slate-200"></div>
    </div>
  );
}

// ── AMOUNT FORMAT ────────────────────────────────────────
export function fmtAmt(val, currency='₹') {
  const n = parseFloat(val) || 0;
  return currency + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}
